import { useEffect, useState } from 'react'
import { Aborted, ApiError, NetworkError } from '../api/client'
import { subscribe } from '../api/events'

export interface ItemQuery<T> {
  item: T | null
  loading: boolean
  error: string | null
  /** Der Server hat mit 403 abgelehnt. */
  denied: boolean
  /** Es gibt den Datensatz nicht — 404. */
  notFound: boolean
}

export interface LiveOptions {
  /**
   * Kanal, auf den gehört wird. Kommt dort ein Hinweis, wird neu geladen.
   *
   * Ohne Einschränkung auf eine Kennung: Gedacht für Kennzahlen und
   * Nebenlisten, die den ganzen Bestand zusammenfassen und von jeder Änderung
   * betroffen sind — gleich, welcher Datensatz sie ausgelöst hat.
   */
  topic?: string
}

/**
 * Lädt einen einzelnen Datensatz und hält seine Zustände.
 *
 * Das Gegenstück zu `useListQuery`, mit einem Unterschied, der eine eigene
 * Angabe verdient: **404**. Bei einer Liste gibt es das nicht — dort ist "keine
 * Treffer" eine leere Seite mit 200. Ein einzelner Datensatz kann dagegen
 * fehlen, und das ist kein Fehler, sondern eine Auskunft: Der Link ist alt, der
 * Datensatz wurde entfernt.
 *
 * Ohne Tipppause: Hier tippt niemand, es wird eine Kennung aus der Adresse
 * geladen.
 */
export function useItemQuery<T>(
  load: (signal: AbortSignal) => Promise<T>,
  enabled: boolean = true,
  live: LiveOptions = {},
): ItemQuery<T> {
  const [item, setItem] = useState<T | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const [notFound, setNotFound] = useState(false)

  // Ein Zähler als zusätzlicher Auslöser: Die Ladefunktion ändert sich beim
  // Ereignis nicht, geladen werden soll trotzdem.
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()

    setLoading(true)
    setError(null)
    setNotFound(false)

    load(controller.signal)
      .then((answer) => {
        setItem(answer)
        setDenied(false)
      })
      .catch((cause) => {
        // Ein Abbruch ist kein Fehler: Er kam, weil schon der nächste Aufruf
        // läuft. Alles zurückzusetzen würde beim Wechsel zwischen zwei
        // Datensätzen kurz eine Fehlermeldung zeigen.
        if (cause instanceof Aborted) return
        if (cause instanceof ApiError && cause.status === 403) setDenied(true)
        else if (cause instanceof ApiError && cause.status === 404) setNotFound(true)
        else if (cause instanceof NetworkError) {
          setError('Der Server ist nicht erreichbar.')
        } else if (cause instanceof ApiError) setError(cause.message)
        else setError(String(cause))
        setItem(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [load, enabled, tick])

  // Der Ereignisstrom. Ohne Sammelfenster wie bei den Listen: Eine
  // zusammenfassende Abfrage ist schnell, und ein Hinweis je Änderung ist
  // kein Andrang.
  const topic = live.topic
  useEffect(() => {
    if (!enabled || !topic) return
    return subscribe(topic, () => setTick((n) => n + 1))
  }, [topic, enabled])

  return { item, loading, error, denied, notFound }
}
